import {
  type CoreEvent,
  type DeviceView,
  type EncryptionStatusView,
  type SearchCoverageView,
  type AuthIntent,
  type LoginFlowsView,
  type LoginIdentifier,
  type OAuthMetadataView,
  type RegistrationFlowsView,
  type SessionInfo,
  type SyncStatus,
  type ProfilePropagationView,
  type ProfileView,
  type RegistrationResultView,
  type VerificationView,
  type Rpc,
  type RpcResult,
  createRpc,
} from '#src/generated/protocol';

import { createMedia } from './media.svelte.js';
import { invalidatePacks, isPackAccountDataEvent } from '#lib/emoji/load-packs.js';
import { createTransport } from '#src/transport/create';
import type { Transport } from '../../transport';
import { CoreError } from '../../transport';
import QuickLRU from 'quick-lru';
import { on } from 'svelte/events';
import { deviceDisplayName } from '#lib/deployment/policy.js';
import {
  defaultE2eePolicy,
  parseE2eePolicy,
  type E2eePolicy,
} from '#lib/deployment/e2ee-policy.js';
import { onDebugLogCapture, recordDebugLog } from '#lib/observability/debug-log.svelte.js';
import { clearRoomListSnapshot } from '#lib/rooms/room-list-snapshot.js';
import { clearRecentSearches } from '#lib/search/recent-searches.svelte.js';
import { reportSessionFailure } from '#lib/observability/session-telemetry.js';
import {
  browserGatesCoreNetwork,
  localNetworkDenied,
  LocalNetworkBlockedError,
} from '#lib/platform/local-network.js';

type WellKnownResponse = { 'm.homeserver'?: { base_url?: unknown } };
export type { OutgoingMentions } from './api/messages.svelte.js';
export type { CreateRoomOptions } from './api/rooms.js';

const profileCacheFreshMs = 2 * 60 * 1000;
const profileFailureRetryMs = 60 * 1000;
const relationsCacheFreshMs = 60 * 1000;
const SLOW_START_MS = 15_000;
const MAX_PROFILE_CACHE_ENTRIES = 256;
const MAX_RELATIONS_CACHE_ENTRIES = 128;
const MAX_PROFILE_LOOKUPS = 24;
const PROFILE_RATE_LIMIT_PAUSE_MS = 1000;
const PROFILE_CHANGE_NOTIFY_MS = 500;
const PROFILE_RETRY_AFTER_CAP_MS = 30_000;

async function discoverBaseUrl(origin: URL): Promise<string | null> {
  try {
    const response = await fetch(new URL('/.well-known/matrix/client', origin), { mode: 'cors' });
    if (!response.ok) return null;
    const body = (await response.json()) as WellKnownResponse;
    const baseUrl = body['m.homeserver']?.base_url;
    if (typeof baseUrl !== 'string') return null;
    return new URL(baseUrl).toString();
  } catch (error) {
    console.warn('[sable auth] page homeserver discovery failed; using entered server', {
      error: error instanceof Error ? error.name : 'unknown',
    });
    return null;
  }
}

function homeserverUrl(homeserver: string): URL {
  return new URL(homeserver.includes('://') ? homeserver : `https://${homeserver}`);
}

async function grantLocalNetworkAccess(baseUrl: string): Promise<boolean> {
  try {
    await fetch(new URL('_matrix/client/versions', homeserverUrl(baseUrl)), { mode: 'cors' });
    return true;
  } catch (error) {
    console.warn('[sable auth] homeserver unreachable from the page', {
      error: error instanceof Error ? error.name : 'unknown',
    });
    return false;
  }
}

async function blameLocalNetwork(error: unknown, homeserver: string): Promise<never> {
  if (
    browserGatesCoreNetwork() &&
    error instanceof CoreError &&
    error.detail.code === 'unavailable' &&
    (await grantLocalNetworkAccess(homeserver))
  ) {
    throw new LocalNetworkBlockedError(homeserverUrl(homeserver).hostname);
  }
  throw error;
}

async function resolveHomeserverInPage(
  homeserver: string,
  cache: Map<string, string>
): Promise<string> {
  const cached = cache.get(homeserver);
  if (cached) return cached;

  let origin: URL;
  try {
    origin = homeserverUrl(homeserver);
  } catch {
    return homeserver;
  }

  if (origin.hostname.toLowerCase().endsWith('.onion')) return homeserver;

  const resolved = await discoverBaseUrl(origin);
  await grantLocalNetworkAccess(resolved ?? origin.toString());
  if (resolved === null) return homeserver;

  cache.set(homeserver, resolved);
  return resolved;
}

export type UserRelations = RpcResult<Rpc['account']['userRelations']>;
export type CoreStatus = 'idle' | 'starting' | 'signed-out' | 'authenticating' | 'ready' | 'error';
export type CoreSession = SessionInfo;
export type ActiveVerification = { userId: string; flowId: string; state: VerificationView };

function discardAccountStore(transport: Transport, accountId: string): void {
  void transport.deleteAccountStore(accountId).catch((error: unknown) => {
    console.error('[sable core] the local store was not deleted', accountId, error);
  });
}

function describeEvent(event: CoreEvent): string {
  switch (event.type) {
    case 'sync_status':
      return event.state === 'error'
        ? `sync_status error: ${event.message}`
        : `sync_status ${event.state}`;
    case 'qr_login': {
      const side = event.grant ? 'grant' : 'login';
      const { progress } = event;
      return progress.stage === 'failed'
        ? `qr_login ${side} failed: ${progress.reason}`
        : `qr_login ${side} ${progress.stage}`;
    }
    case 'presence':
      return `presence ${event.presence}`;
    default:
      return event.type;
  }
}

export class CoreClient {
  status = $state<CoreStatus>('idle');
  session = $state<CoreSession | null>(null);
  private lockedAccountId = $state<string | null>(null);
  accountLocked = $derived(
    this.lockedAccountId !== null && this.lockedAccountId === this.session?.account_id
  );
  reauthenticationAccountId = $state<string | null>(null);
  accounts = $state.raw<CoreSession[]>([]);
  verification = $state<ActiveVerification | null>(null);
  crashed = $state<string | null>(null);
  storageInterrupted = $state(false);
  superseded = $state(false);
  restoreFailed = $state(false);
  slowStart = $state(false);
  sync = $state<SyncStatus | null>(null);
  /** This device's own verification and recovery state, pushed on change. */
  encryption = $state<EncryptionStatusView | null>(null);
  searchCoverage = $state<SearchCoverageView | null>(null);
  searchCoverageUnavailable = $state(false);
  /** Every device on this account, replaced whole on change. */
  deviceList = $state.raw<DeviceView[]>([]);
  /** The homeserver's OAuth metadata: `undefined` until known, `null` when it has no OAuth. */
  oauthMetadata = $state.raw<OAuthMetadataView | null | undefined>(undefined);
  unresponsive = $state(false);
  localNetworkBlocked = $state<string | null>(null);
  accountRevision = $state(0);

  private transport: Transport | null = null;
  private unsubscribeTransport: (() => void) | null = null;
  private startPromise: Promise<void> | null = null;
  private generation = 0;
  private encryptionEvents = 0;
  private deviceEvents = 0;
  private readonly accountChannel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('sable-active-account');
  /* Nothing renders from these, and a reactive map would make every mounted
     profile card re-run its effect on any other user's cache write. */
  /* eslint-disable svelte/prefer-svelte-reactivity */
  private readonly profileCache = new QuickLRU<
    string,
    { accountId: string | null; profile: ProfileView; freshUntil: number }
  >({ maxSize: MAX_PROFILE_CACHE_ENTRIES });
  private readonly profileRequests = new Map<
    string,
    {
      accountId: string | null;
      request: Promise<ProfileView>;
      waiters: number;
      cancel: () => void;
    }
  >();
  private readonly profileFailures = new QuickLRU<
    string,
    { accountId: string | null; error: unknown }
  >({ maxSize: MAX_PROFILE_CACHE_ENTRIES, maxAge: profileFailureRetryMs });
  private readonly profileChangeListeners = new Set<(userId: string) => void>();
  private readonly changedProfiles = new Set<string>();
  private profileChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private profileLookups = 0;
  private readonly profileLookupQueue: {
    resolve: () => void;
    reject: (reason: unknown) => void;
  }[] = [];
  private profileResumeAt = 0;
  private readonly relationsCache = new QuickLRU<
    string,
    { accountId: string | null; relations: UserRelations }
  >({ maxSize: MAX_RELATIONS_CACHE_ENTRIES, maxAge: relationsCacheFreshMs });
  private readonly resolvedHomeservers = new Map<string, string>();
  private readonly e2eePolicies = new Map<string, Promise<E2eePolicy>>();
  /* eslint-enable svelte/prefer-svelte-reactivity */

  private stopAccountChannel: (() => void) | null = null;

  readonly media = createMedia(() => this.ensureTransport());

  readonly rpc = createRpc((method, params) => this.ensureTransport().call(method, params));

  /** One client per transport, for flows that must finish on the transport they started on. */
  readonly #pinned = new WeakMap<Transport, Rpc>();

  #rpcOn(transport: Transport): Rpc {
    let rpc = this.#pinned.get(transport);
    if (!rpc) {
      rpc = createRpc((method, params) => transport.call(method, params));
      this.#pinned.set(transport, rpc);
    }
    return rpc;
  }

  constructor(private readonly openTransport: () => Transport = createTransport) {
    if (this.accountChannel) {
      this.stopAccountChannel = on(this.accountChannel, 'message', () => {
        void this.syncAccountFromWorker();
      });
    }
  }

  async start(): Promise<void> {
    if (this.startPromise) return this.startPromise;

    const promise = this.startTransport();
    this.startPromise = promise;

    try {
      await promise;
    } finally {
      if (this.startPromise === promise) this.startPromise = null;
    }
  }

  /** `start()` would otherwise join the stuck attempt. */
  retryStart(): Promise<void> {
    this.startPromise = null;
    return this.start();
  }

  async login(
    homeserver: string,
    identifier: LoginIdentifier,
    password: string,
    reauthAccountId?: string
  ): Promise<void> {
    let transport: Transport;
    try {
      transport = this.ensureTransport();
    } catch (error) {
      this.status = 'error';
      throw error;
    }

    const generation = ++this.generation;
    const previousSession = this.session;
    this.status = 'authenticating';

    try {
      const resolvedHomeserver = await resolveHomeserverInPage(
        homeserver,
        this.resolvedHomeservers
      );
      const userId = await this.#rpcOn(transport)
        .auth.login({
          reauth_account_id: reauthAccountId ?? null,
          homeserver: resolvedHomeserver,
          identifier,
          password,
          device_display_name: deviceDisplayName(),
        })
        .catch((error: unknown) => blameLocalNetwork(error, resolvedHomeserver));

      if (generation !== this.generation || transport !== this.transport) return;

      await this.finishAuthentication(userId, generation);
    } catch (error) {
      if (generation === this.generation && transport === this.transport) {
        this.replaceSession(previousSession);
        this.status = previousSession ? 'ready' : this.statusAfterAuthenticationError(error);
      }
      throw error;
    }
  }

  async loginFlows(homeserver: string): Promise<LoginFlowsView> {
    const transport = this.ensureTransport();
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    return this.#rpcOn(transport)
      .auth.loginFlows({ homeserver: resolvedHomeserver })
      .catch((error: unknown) => blameLocalNetwork(error, resolvedHomeserver));
  }

  async registrationFlows(homeserver: string): Promise<RegistrationFlowsView> {
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    return this.rpc.auth.registrationFlows({ homeserver: resolvedHomeserver });
  }

  async requestPasswordResetEmail(
    homeserver: string,
    email: string,
    clientSecret: string | null,
    sendAttempt: number
  ): Promise<{ clientSecret: string; sid: string }> {
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    const response = await this.rpc.auth.requestPasswordResetEmail({
      homeserver: resolvedHomeserver,
      email,
      client_secret: clientSecret,
      send_attempt: sendAttempt,
    });
    return { clientSecret: response.client_secret, sid: response.sid };
  }

  async resetPassword(
    homeserver: string,
    clientSecret: string,
    sid: string,
    newPassword: string,
    logoutDevices: boolean
  ): Promise<void> {
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    await this.rpc.auth.resetPassword({
      homeserver: resolvedHomeserver,
      client_secret: clientSecret,
      sid,
      new_password: newPassword,
      logout_devices: logoutDevices,
    });
  }

  async register(
    homeserver: string,
    username: string,
    password: string,
    registrationEmail: string | null = null,
    registrationToken: string | null = null
  ): Promise<RegistrationResultView> {
    let transport: Transport;
    try {
      transport = this.ensureTransport();
    } catch (error) {
      this.status = 'error';
      throw error;
    }

    const previousSession = this.session;
    this.status = 'authenticating';
    try {
      const resolvedHomeserver = await resolveHomeserverInPage(
        homeserver,
        this.resolvedHomeservers
      );
      const result = await this.#rpcOn(transport).auth.register({
        homeserver: resolvedHomeserver,
        username,
        password,
        registration_email: registrationEmail,
        registration_token: registrationToken,
        device_display_name: deviceDisplayName(),
      });
      if (result.state === 'complete') {
        await this.finishAuthentication(result.user_id);
      } else {
        this.replaceSession(previousSession);
        this.status = previousSession ? 'ready' : 'signed-out';
      }
      return result;
    } catch (error) {
      this.replaceSession(previousSession);
      this.status = previousSession ? 'ready' : this.statusAfterAuthenticationError(error);
      throw error;
    }
  }

  async continueRegistration(): Promise<RegistrationResultView> {
    const result = await this.rpc.auth.continueRegistration();
    if (result.state === 'complete') {
      await this.finishAuthentication(result.user_id);
    }
    return result;
  }

  async startOidcLogin(
    homeserver: string,
    redirectUri: string,
    intent: AuthIntent = 'login',
    reauthAccountId?: string
  ): Promise<string> {
    const transport = this.ensureTransport();
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    return this.#rpcOn(transport).auth.startOidcLogin({
      reauth_account_id: reauthAccountId ?? null,
      homeserver: resolvedHomeserver,
      redirect_uri: redirectUri,
      intent,
      device_display_name: deviceDisplayName(),
    });
  }

  async completeOidcLogin(callbackUrl: string): Promise<void> {
    let transport: Transport;
    try {
      transport = this.ensureTransport();
    } catch (error) {
      this.status = 'error';
      throw error;
    }

    const generation = ++this.generation;
    const previousSession = this.session;
    this.status = 'authenticating';

    try {
      const userId = await this.#rpcOn(transport).auth.completeOidcLogin({
        callback_url: callbackUrl,
      });

      if (generation !== this.generation || transport !== this.transport) return;

      await this.finishAuthentication(userId, generation);
    } catch (error) {
      if (generation === this.generation && transport === this.transport) {
        this.replaceSession(previousSession);
        this.status = previousSession ? 'ready' : this.statusAfterAuthenticationError(error);
      }
      throw error;
    }
  }

  async startQrLogin(
    homeserver: string | null,
    redirectUri: string,
    scanned: string | null
  ): Promise<void> {
    const transport = this.ensureTransport();
    const resolved =
      homeserver === null
        ? null
        : await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    await this.#rpcOn(transport).auth.startQrLogin({
      homeserver: resolved,
      redirect_uri: redirectUri,
      scanned,
    });
  }

  async finishQrLogin(userId: string): Promise<void> {
    await this.finishAuthentication(userId);
  }

  async startQrGrant(scanned: string | null): Promise<void> {
    await this.rpc.auth.startQrGrant({ scanned });
  }

  async qrCheckCode(code: number): Promise<void> {
    await this.rpc.auth.qrCheckCode({ code });
  }

  async qrGrantContinue(confirm: boolean): Promise<void> {
    await this.rpc.auth.qrGrantContinue({ confirm });
  }

  async cancelQr(): Promise<void> {
    await this.rpc.auth.cancelQr();
  }

  async startSsoLogin(
    homeserver: string,
    redirectUri: string,
    idpId?: string,
    intent: AuthIntent = 'login',
    reauthAccountId?: string
  ): Promise<string> {
    const transport = this.ensureTransport();
    const resolvedHomeserver = await resolveHomeserverInPage(homeserver, this.resolvedHomeservers);
    return this.#rpcOn(transport).auth.startSsoLogin({
      reauth_account_id: reauthAccountId ?? null,
      homeserver: resolvedHomeserver,
      redirect_uri: redirectUri,
      idp_id: idpId ?? null,
      intent,
    });
  }

  async completeSsoLogin(callbackUrl: string): Promise<void> {
    let transport: Transport;
    try {
      transport = this.ensureTransport();
    } catch (error) {
      this.status = 'error';
      throw error;
    }

    const generation = ++this.generation;
    const previousSession = this.session;
    this.status = 'authenticating';

    try {
      const userId = await this.#rpcOn(transport).auth.completeSsoLogin({
        callback_url: callbackUrl,
        device_display_name: deviceDisplayName(),
      });

      if (generation !== this.generation || transport !== this.transport) return;

      await this.finishAuthentication(userId, generation);
    } catch (error) {
      if (generation === this.generation && transport === this.transport) {
        this.replaceSession(previousSession);
        this.status = previousSession ? 'ready' : this.statusAfterAuthenticationError(error);
      }
      throw error;
    }
  }

  cachedUserProfile(userId: string, allowStale = false): ProfileView | null {
    const cached = this.profileCache.get(userId);
    return cached?.accountId === (this.session?.account_id ?? null) &&
      (allowStale || cached.freshUntil > Date.now())
      ? cached.profile
      : null;
  }

  async userProfile(userId: string, urgent = false, signal?: AbortSignal): Promise<ProfileView> {
    const accountId = this.session?.account_id ?? null;
    const cached = this.profileCache.get(userId);
    if (cached?.accountId === accountId && cached.freshUntil > Date.now()) {
      return cached.profile;
    }

    const pending = this.profileRequests.get(userId);
    if (pending?.accountId === accountId && !urgent) return this.awaitProfile(pending, signal);

    const failure = this.profileFailures.get(userId);
    if (failure?.accountId === accountId && !urgent) {
      throw failure.error;
    }

    const queued: { cancel: (() => void) | null } = { cancel: null };
    const request = this.lookUpProfile(userId, urgent, (cancelQueued) => {
      queued.cancel = cancelQueued;
    })
      .then((profile) => {
        if (this.profileRequests.get(userId)?.request === request) {
          this.profileFailures.delete(userId);
          this.profileCache.set(userId, {
            accountId,
            profile,
            freshUntil: Date.now() + profileCacheFreshMs,
          });
        }
        return profile;
      })
      .catch((error: unknown) => {
        const rateLimited = error instanceof CoreError && error.detail.code === 'rate_limited';
        const cancelled = error instanceof DOMException && error.name === 'AbortError';
        if (!rateLimited && !cancelled && this.profileRequests.get(userId)?.request === request) {
          this.profileFailures.set(userId, { accountId, error });
        }
        throw error;
      });
    const entry = {
      accountId,
      request,
      waiters: 0,
      cancel: () => {
        queued.cancel?.();
      },
    };
    this.profileRequests.set(userId, entry);
    const clearRequest = () => {
      if (this.profileRequests.get(userId)?.request === request) {
        this.profileRequests.delete(userId);
      }
    };
    void request.then(clearRequest, clearRequest);
    return this.awaitProfile(entry, signal);
  }

  private awaitProfile(
    entry: { request: Promise<ProfileView>; waiters: number; cancel: () => void },
    signal?: AbortSignal
  ): Promise<ProfileView> {
    entry.waiters += 1;
    if (!signal) return entry.request;
    return new Promise<ProfileView>((resolve, reject) => {
      const onAbort = () => {
        entry.waiters -= 1;
        if (entry.waiters === 0) entry.cancel();
        reject(new DOMException('The profile lookup was cancelled', 'AbortError'));
      };
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
      const settled = () => {
        signal.removeEventListener('abort', onAbort);
      };
      entry.request.then(resolve, reject).finally(settled);
    });
  }

  onProfileChanged(listener: (userId: string) => void): () => void {
    this.profileChangeListeners.add(listener);
    return () => this.profileChangeListeners.delete(listener);
  }

  refreshUserProfile(userId: string): Promise<ProfileView> {
    this.invalidateProfile(userId);
    return this.userProfile(userId, true);
  }

  forgetProfileFailure(userId: string): void {
    this.profileFailures.delete(userId);
  }

  private invalidateProfile(userId: string): void {
    this.profileCache.delete(userId);
    this.profileFailures.delete(userId);
    this.profileRequests.delete(userId);
    this.changedProfiles.add(userId);
    this.profileChangeTimer ??= setTimeout(() => {
      this.profileChangeTimer = null;
      const changed = [...this.changedProfiles];
      this.changedProfiles.clear();
      for (const id of changed) {
        for (const listener of this.profileChangeListeners) listener(id);
      }
    }, PROFILE_CHANGE_NOTIFY_MS);
  }

  private async lookUpProfile(
    userId: string,
    urgent: boolean,
    onQueued: (cancel: () => void) => void
  ): Promise<ProfileView> {
    if (urgent) return this.requestProfile(userId, true);
    if (this.profileLookups < MAX_PROFILE_LOOKUPS) this.profileLookups += 1;
    else {
      await new Promise<void>((resolve, reject) => {
        const waiting = { resolve, reject };
        this.profileLookupQueue.push(waiting);
        onQueued(() => {
          const index = this.profileLookupQueue.indexOf(waiting);
          if (index === -1) return;
          this.profileLookupQueue.splice(index, 1);
          reject(new DOMException('The profile lookup was cancelled', 'AbortError'));
        });
      });
    }
    try {
      return await this.requestProfile(userId, false);
    } finally {
      const next = this.profileLookupQueue.pop();
      if (next) next.resolve();
      else this.profileLookups -= 1;
    }
  }

  private async requestProfile(userId: string, urgent: boolean): Promise<ProfileView> {
    for (let retried = false; ; retried = true) {
      if (!urgent || retried) {
        for (
          let wait = this.profileResumeAt - Date.now();
          wait > 0;
          wait = this.profileResumeAt - Date.now()
        ) {
          await new Promise((resolve) => setTimeout(resolve, wait));
        }
      }
      try {
        return await this.rpc.account.userProfile({ user_id: userId });
      } catch (error) {
        if (!(error instanceof CoreError) || error.detail.code !== 'rate_limited') throw error;
        this.profileResumeAt = Math.max(
          this.profileResumeAt,
          Date.now() +
            Math.min(
              error.detail.retry_after_ms ?? PROFILE_RATE_LIMIT_PAUSE_MS,
              PROFILE_RETRY_AFTER_CAP_MS
            )
        );
        if (retried) throw error;
      }
    }
  }

  /**
   * Rooms shared with this user and whether the account ignores them; cached because the core reads membership per room.
   */
  async userRelations(userId: string): Promise<UserRelations> {
    const accountId = this.session?.account_id ?? null;
    const cached = this.relationsCache.get(userId);
    if (cached?.accountId === accountId) {
      return cached.relations;
    }

    const response = await this.rpc.account.userRelations({ user_id: userId });
    const relations = response;
    this.relationsCache.set(userId, { accountId, relations });
    return relations;
  }

  e2eePolicy(): Promise<E2eePolicy> {
    const accountId = this.session?.account_id;
    if (accountId === undefined) return Promise.resolve(defaultE2eePolicy);
    let policy = this.e2eePolicies.get(accountId);
    if (policy === undefined) {
      policy = this.rpc.account.wellKnown().then(parseE2eePolicy, () => {
        this.e2eePolicies.delete(accountId);
        return defaultE2eePolicy;
      });
      this.e2eePolicies.set(accountId, policy);
    }
    return policy;
  }

  async setUserIgnored(userId: string, ignored: boolean): Promise<void> {
    await (ignored ? this.rpc.account.ignoreUser : this.rpc.account.unignoreUser)({
      user_id: userId,
    });
    this.relationsCache.delete(userId);
  }

  async refreshSearchCoverage(attempts = 3): Promise<void> {
    const generation = this.generation;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const coverage = await this.rpc.search.coverage();
        if (generation !== this.generation) return;
        this.searchCoverage = coverage;
        this.searchCoverageUnavailable = false;
        return;
      } catch (error) {
        if (generation !== this.generation) return;
        console.warn('[sable core] search coverage unavailable', error);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    if (generation === this.generation) this.searchCoverageUnavailable = true;
  }

  async switchAccount(accountId: string): Promise<void> {
    const generation = this.generation;
    const transport = this.ensureTransport();
    const slow = setTimeout(() => {
      reportSessionFailure('switch_slow');
    }, SLOW_START_MS);
    let session: CoreSession;
    try {
      session = await this.#rpcOn(transport).session.switchAccount({
        account_id: accountId,
      });
    } catch (error) {
      reportSessionFailure('switch', error);
      throw error;
    } finally {
      clearTimeout(slow);
    }
    if (generation !== this.generation || transport !== this.transport) return;
    await this.refreshAccounts();
    if (generation !== this.generation || transport !== this.transport) return;
    this.replaceSession(session);
    this.restoreFailed = false;
    this.crashed = null;
    this.storageInterrupted = false;
    this.status = 'ready';
  }

  beginSignInRecovery(): void {
    this.generation += 1;
    this.startPromise = null;
    this.cleanupTransport();
    this.replaceSession(null, false);
    this.reauthenticationAccountId = null;
    this.restoreFailed = false;
    this.status = 'signed-out';
  }

  async removeAccount(accountId: string): Promise<void> {
    const transport = this.ensureTransport();
    const userId = this.accounts.find((account) => account.account_id === accountId)?.user_id;
    await this.#rpcOn(transport).session.removeAccount({ account_id: accountId });
    await this.refreshAccounts();
    discardAccountStore(transport, accountId);
    clearRoomListSnapshot(accountId);
    if (userId !== undefined) clearRecentSearches(userId);
  }

  async logout(): Promise<void> {
    const transport = this.ensureTransport();
    const accountId = this.session?.account_id ?? null;
    const userId = this.session?.user_id ?? null;
    await this.#rpcOn(transport).session.logout();
    this.generation += 1;
    this.replaceSession(null);
    this.verification = null;
    this.status = 'authenticating';
    if (accountId !== null) {
      discardAccountStore(transport, accountId);
      clearRoomListSnapshot(accountId);
    }
    if (userId !== null) clearRecentSearches(userId);

    try {
      await this.refreshAccounts();
      const fallbackAccountId = this.accounts.find((account) => !account.needs_reauth)?.account_id;
      if (fallbackAccountId !== undefined) {
        await this.switchAccount(fallbackAccountId);
        return;
      }
      this.status = 'signed-out';
    } catch (error) {
      this.status = 'signed-out';
      throw error;
    }
  }

  async resetCaches(): Promise<void> {
    const accountIds = this.accounts.map((account) => account.account_id);
    await this.ensureTransport().resetCaches(accountIds);
    for (const accountId of accountIds) clearRoomListSnapshot(accountId);
  }

  async requestVerification(userId: string, deviceId: string | null = null): Promise<string> {
    const pending = this.verification;
    if (
      pending?.userId === userId &&
      pending.state.phase === 'requested' &&
      !pending.state.initiated_by_us
    ) {
      await this.rpc.encryption.acceptVerification({ user_id: userId, flow_id: pending.flowId });
      return pending.flowId;
    }
    const flowId = await this.rpc.encryption.requestVerification({
      user_id: userId,
      device_id: deviceId,
    });
    this.verification = {
      userId,
      flowId,
      state: {
        phase: 'requested',
        is_self: userId === this.session?.user_id,
        initiated_by_us: true,
      },
    };
    return flowId;
  }

  async setProfileField(field: string, value: unknown): Promise<void> {
    await this.rpc.account.setProfileField({ field, value });
    const userId = this.session?.user_id;
    if (userId) this.invalidateProfile(userId);
  }

  async uploadRoomAvatar(
    roomId: string,
    mime: string,
    bytes: Uint8Array<ArrayBuffer>
  ): Promise<string> {
    const uri = await this.ensureTransport().uploadMedia(mime, bytes);
    await this.rpc.room_state.setAvatar({ room_id: roomId, url: uri });
    return uri;
  }

  async uploadAvatar(
    mime: string,
    bytes: Uint8Array<ArrayBuffer>,
    propagateTo: ProfilePropagationView
  ): Promise<string> {
    const uri = await this.ensureTransport().uploadMedia(mime, bytes);
    await this.rpc.account.setAvatarUrl({ url: uri, propagate_to: propagateTo });
    return uri;
  }

  subscribeEvents(onEvent: (event: CoreEvent) => void): () => void {
    return this.ensureTransport().subscribe((event) => {
      if (event.type === 'verification') {
        const isTerminal = event.state.phase === 'done' || event.state.phase === 'cancelled';
        if (!isTerminal || this.verification?.flowId === event.flow_id) {
          this.verification = {
            userId: event.user_id,
            flowId: event.flow_id,
            state: event.state,
          };
        }
      }
      onEvent(event);
    });
  }

  stop(): void {
    this.generation += 1;
    this.startPromise = null;
    this.cleanupTransport();
    this.replaceSession(null, false);
    this.verification = null;
    this.resetCachedState();
    this.restoreFailed = false;
    this.status = 'idle';
    this.stopAccountChannel?.();
    this.stopAccountChannel = null;
    this.accountChannel?.close();
  }

  private resetCachedState(): void {
    this.profileCache.clear();
    this.profileRequests.clear();
    this.profileFailures.clear();
    this.changedProfiles.clear();
    if (this.profileChangeTimer !== null) clearTimeout(this.profileChangeTimer);
    this.profileChangeTimer = null;
    this.relationsCache.clear();
    this.sync = null;
    this.crashed = null;
    this.unresponsive = false;
    this.encryption = null;
    this.deviceList = [];
    this.oauthMetadata = undefined;
    this.searchCoverage = null;
    this.searchCoverageUnavailable = false;
    this.localNetworkBlocked = null;
  }

  /** Both events fire only on a change, so a session that starts unverified
      would otherwise report nothing. */
  private async primeEncryptionStatus(): Promise<void> {
    const revision = this.accountRevision;
    const reported = this.encryptionEvents;
    const reportedDevices = this.deviceEvents;
    try {
      const [status, devices] = await Promise.all([
        this.rpc.encryption.status(),
        this.rpc.encryption.devices(),
      ]);
      if (revision !== this.accountRevision) return;
      if (reported === this.encryptionEvents) this.encryption = status;
      if (reportedDevices === this.deviceEvents) this.deviceList = devices.devices;
    } catch (error) {
      console.debug('[sable core] encryption status unavailable', error);
    }
  }

  private replaceSession(session: CoreSession | null, broadcast = true): void {
    const changed = this.session?.account_id !== session?.account_id;
    if (changed) {
      this.accountRevision += 1;
      this.resetCachedState();
    }
    this.session = session;
    if (session) this.reauthenticationAccountId = null;
    if (changed && session) {
      void this.primeEncryptionStatus();
      void this.primeSyncStatus();
      void this.primeOAuthMetadata();
      void this.primeOwnProfile(session.user_id);
      if (browserGatesCoreNetwork()) void grantLocalNetworkAccess(session.homeserver);
    }
    if (changed && broadcast) this.accountChannel?.postMessage(null);
  }

  private async syncAccountFromWorker(): Promise<void> {
    try {
      const session = await this.rpc.session.restore();
      if (session) {
        this.replaceSession(session);
        await this.refreshAccounts();
        this.status = 'ready';
      } else {
        this.replaceSession(null);
        this.accounts = [];
        this.status = 'signed-out';
      }
    } catch (error) {
      // The worker may have closed before this tab receives the broadcast.
      console.debug('[sable core] session not followed after broadcast', error);
    }
  }

  private async startTransport(): Promise<void> {
    const generation = ++this.generation;
    this.status = 'starting';
    this.slowStart = false;
    const slow = setTimeout(() => {
      if (generation !== this.generation || this.status !== 'starting') return;
      this.slowStart = true;
      reportSessionFailure('restore_slow');
    }, SLOW_START_MS);

    try {
      const transport = this.ensureTransport();
      const session = await this.#rpcOn(transport).session.restore();
      if (generation !== this.generation) return;

      if (session) {
        this.replaceSession(session);
        await this.refreshAccounts();
        if (generation !== this.generation) return;
        this.restoreFailed = false;
        this.crashed = null;
        this.storageInterrupted = false;
        this.status = 'ready';
      } else {
        await this.refreshAccounts();
        if (generation !== this.generation) return;
        const fallback = this.accounts.find((account) => !account.needs_reauth);
        if (fallback) {
          await this.switchAccount(fallback.account_id);
          return;
        }
        this.replaceSession(null);
        this.reauthenticationAccountId =
          this.accounts.find((account) => account.needs_reauth)?.account_id ?? null;
        this.restoreFailed = false;
        this.status = 'signed-out';
      }
    } catch (error) {
      if (generation !== this.generation) return;

      console.error('[sable core] restore failed', error);
      reportSessionFailure('restore', error);
      if (error instanceof CoreError) {
        try {
          await this.refreshAccounts();
        } catch (refreshError) {
          // Keep the last known accounts if storage is unavailable.
          console.debug('[sable core] accounts not refreshed after restore failure', refreshError);
        }
        if (generation !== this.generation) return;
      }
      this.restoreFailed = true;
      this.status = 'error';
      this.cleanupTransport();
    } finally {
      clearTimeout(slow);
      if (generation === this.generation) this.slowStart = false;
    }
  }

  private ensureTransport(): Transport {
    if (this.transport) return this.transport;

    const transport = this.openTransport();
    this.transport = transport;
    const unsubscribeEvents = transport.subscribe(this.handleEvent);
    const unsubscribeCrash = transport.subscribeCrash((message) => {
      this.crashed = message;
      this.accountRevision += 1;
      this.cleanupTransport();
      this.status = 'error';
    });
    const unsubscribeStorageFailure = transport.subscribeStorageFailure?.(() => {
      this.storageInterrupted = true;
    });
    // The closed transport stays in place so nothing reopens a worker here.
    const unsubscribeSuperseded = transport.subscribeSuperseded?.(() => {
      this.superseded = true;
    });
    const unsubscribeStall = transport.subscribeStall((stalled) => {
      this.unresponsive = stalled;
    });
    const stopLogCapture = onDebugLogCapture((enabled) => {
      transport.setDebugLogs(enabled);
    });
    this.unsubscribeTransport = () => {
      unsubscribeEvents();
      unsubscribeCrash();
      unsubscribeStorageFailure?.();
      unsubscribeSuperseded?.();
      unsubscribeStall();
      stopLogCapture();
    };
    return transport;
  }

  private statusAfterAuthenticationError(error: unknown): CoreStatus {
    if (error instanceof CoreError) {
      switch (error.detail.code) {
        case 'denied':
        case 'rate_limited':
        case 'unsupported':
        case 'unknown_homeserver':
        case 'registration_unavailable':
        case 'username_taken':
        case 'invalid_username':
        case 'invalid_email':
        case 'email_verification_failed':
        case 'weak_password':
        case 'registration_stage_failed':
          return 'signed-out';
      }
    }
    return 'error';
  }

  private async primeOwnProfile(userId: string): Promise<void> {
    try {
      await this.userProfile(userId);
    } catch (error) {
      this.forgetProfileFailure(userId);
      console.debug('[sable core] own profile unavailable', error);
    }
  }

  private async primeSyncStatus(): Promise<void> {
    const revision = this.accountRevision;
    try {
      const status = await this.rpc.session.syncStatus();
      if (revision !== this.accountRevision || this.sync !== null) return;
      this.applySyncStatus(status);
    } catch (error) {
      console.debug('[sable core] sync status unavailable', error);
    }
  }

  private async primeOAuthMetadata(): Promise<void> {
    const revision = this.accountRevision;
    try {
      const metadata = await this.rpc.session.oauthMetadata();
      if (revision !== this.accountRevision || this.oauthMetadata !== undefined) return;
      this.oauthMetadata = metadata;
    } catch (error) {
      console.debug('[sable core] OAuth metadata unavailable', error);
    }
  }

  private applySyncStatus(status: SyncStatus): void {
    this.sync = status;
    if (status.state !== 'offline') {
      this.localNetworkBlocked = null;
      return;
    }
    if (browserGatesCoreNetwork()) void this.checkLocalNetwork();
  }

  private async checkLocalNetwork(): Promise<void> {
    const session = this.session;
    if (!session) return;
    const revision = this.accountRevision;
    const blocked =
      (await grantLocalNetworkAccess(session.homeserver)) ||
      (navigator.onLine && (await localNetworkDenied()));
    if (revision !== this.accountRevision || this.sync?.state !== 'offline') return;
    this.localNetworkBlocked = blocked ? homeserverUrl(session.homeserver).hostname : null;
  }

  private readonly handleEvent = (event: CoreEvent): void => {
    recordDebugLog(
      'debug',
      event.type === 'sync_status' ? 'sync' : 'general',
      'core',
      describeEvent(event)
    );
    switch (event.type) {
      case 'account_lock_changed':
        if (event.locked) this.lockedAccountId = event.account_id;
        else if (this.lockedAccountId === event.account_id) this.lockedAccountId = null;
        return;
      case 'sync_status':
        this.applySyncStatus(event);
        return;
      case 'encryption_status':
        this.encryptionEvents += 1;
        this.encryption = event.status;
        return;
      case 'devices_changed':
        this.deviceEvents += 1;
        this.deviceList = event.devices;
        return;
      case 'oauth_metadata':
        this.oauthMetadata = event.metadata;
        return;
      case 'profile_changed':
        this.invalidateProfile(event.user_id);
        return;
      case 'nicknames_changed':
        for (const userId of event.user_ids) this.invalidateProfile(userId);
        return;
      case 'search_coverage':
        this.searchCoverage = event.coverage;
        this.searchCoverageUnavailable = false;
        return;
      case 'account_data_changed':
        if (isPackAccountDataEvent(event.event_type)) invalidatePacks(this.rpc);
        return;
      case 'image_packs_changed':
        invalidatePacks(this.rpc);
        return;
      case 'session_ended':
        reportSessionFailure('session_ended', undefined, event.reason);
        this.reauthenticationAccountId = this.session?.account_id ?? null;
        this.replaceSession(null);
        this.status = 'authenticating';
        void this.restoreFallbackAccount();
        return;
      default:
        return;
    }
  };

  private async refreshAccounts(): Promise<void> {
    this.accounts = await this.rpc.session.listAccounts();
  }

  private async finishAuthentication(userId: string, generation = this.generation): Promise<void> {
    const transport = this.ensureTransport();
    const session = await this.#rpcOn(transport).session.restore();
    if (generation !== this.generation || transport !== this.transport) return;
    if (!session || session.user_id !== userId) {
      throw new CoreError({ code: 'not_logged_in' });
    }
    await this.refreshAccounts();
    if (generation !== this.generation || transport !== this.transport) return;
    this.replaceSession(session);
    this.status = 'ready';
  }

  private async restoreFallbackAccount(): Promise<void> {
    const generation = this.generation;
    try {
      await this.refreshAccounts();
      if (generation !== this.generation) return;
      const fallbackAccountId = this.accounts.find((account) => !account.needs_reauth)?.account_id;
      if (fallbackAccountId === undefined) {
        this.status = 'signed-out';
        return;
      }
      await this.switchAccount(fallbackAccountId);
    } catch (error) {
      if (generation !== this.generation) return;
      reportSessionFailure('restore_fallback', error);
      this.restoreFailed = true;
      this.status = 'error';
      this.cleanupTransport();
    }
  }

  private cleanupTransport(): void {
    const unsubscribe = this.unsubscribeTransport;
    const transport = this.transport;
    this.unsubscribeTransport = null;
    this.transport = null;

    try {
      unsubscribe?.();
    } catch (error) {
      // Cleanup should continue even if a transport subscription fails.
      console.debug('[sable core] transport subscription not released', error);
    }

    try {
      transport?.close();
    } catch (error) {
      // Closing an already-closed transport is safe to ignore.
      console.debug('[sable core] transport not closed', error);
    }
  }
}

export function createCoreClient(openTransport?: () => Transport): CoreClient {
  return new CoreClient(openTransport);
}
